import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile as updateFirebaseProfile,
  getAuth,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { auth, db } from '../firebase';
import app from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            setUser({ ...firebaseUser, ...userDoc.data(), id: firebaseUser.uid });
          } else {
            const creationTime = new Date(firebaseUser.metadata.creationTime).getTime();
            const now = new Date().getTime();
            const isNewAccount = (now - creationTime) < 60000;
            
            if (isNewAccount) {
              console.log("New account detected, waiting for profile creation...");
            } else {
              console.warn("User profile not found. Access denied.");
              await signOut(auth);
              setUser(null);
            }
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password.';
      }
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("Reset password error:", error);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password, userData) => {
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);

      await updateFirebaseProfile(newUser, {
        displayName: userData.name,
        photoURL: userData.avatar || ''
      });

      const userProfile = {
        name: userData.name,
        email: email,
        role: 'super_admin',
        department: userData.department || 'Management',
        jobTitle: userData.jobTitle || 'Owner',
        avatar: userData.avatar || '',
        joinDate: new Date().toISOString(),
        status: 'active',
        performance: {
          score: 0,
          tasksCompleted: 0,
          avgRating: 0
        },
        leaveBalance: {
          casual: 12,
          paid: 18
        },
        ...userData
      };

      await setDoc(doc(db, 'users', newUser.uid), userProfile);
      setUser({ ...newUser, ...userProfile, id: newUser.uid });
      return { success: true, role: 'super_admin' };
    } catch (error) {
      console.error("Signup error:", error);
      return { success: false, error: error.message };
    }
  };

  const createTeamMember = async (email, password, userData) => {
    let secondaryApp;
    try {
      secondaryApp = initializeApp(app.options, "SecondaryApp");
      const secondaryAuth = getAuth(secondaryApp);
      
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const newUser = userCredential.user;

      await updateFirebaseProfile(newUser, {
        displayName: userData.name,
        photoURL: userData.avatar || ''
      });

      const userProfile = {
        name: userData.name,
        email: email,
        role: userData.role || 'team_member',
        department: userData.department || '',
        jobTitle: userData.jobTitle || '',
        avatar: userData.avatar || '',
        phone: userData.phone || '',
        location: userData.location || '',
        bio: userData.bio || '',
        joinDate: new Date().toISOString(),
        status: 'active',
        performance: {
          score: 0,
          tasksCompleted: 0,
          avgRating: 0
        },
        leaveBalance: userData.leaveBalance || {
          casual: 12,
          paid: 18
        }
      };

      await setDoc(doc(db, 'users', newUser.uid), userProfile);
      
      await signOut(secondaryAuth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      if (secondaryApp) {
        await deleteApp(secondaryApp);
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const updateProfile = async (updates) => {
    if (!auth.currentUser) return { success: false, error: 'No user logged in' };
    
    try {
      if (updates.name || updates.avatar) {
        await updateFirebaseProfile(auth.currentUser, {
          displayName: updates.name || auth.currentUser.displayName,
          photoURL: updates.avatar || auth.currentUser.photoURL
        });
      }

      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, updates);
      
      setUser(prev => ({ ...prev, ...updates }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const role = user.role || 'team_member';
    const permissions = {
      super_admin: ['all'],
      // Added 'manage_team' to managers
      manager: ['view_team', 'manage_team', 'assign_tasks', 'approve_work', 'view_reports', 'update_tasks', 'delete_tasks'],
      team_member: ['view_own_tasks', 'update_tasks', 'add_comments']
    };

    const userPermissions = permissions[role] || [];
    return userPermissions.includes('all') || userPermissions.includes(permission);
  };

  const value = {
    user,
    login,
    signup,
    resetPassword,
    createTeamMember,
    logout,
    updateProfile,
    hasPermission,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};